import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Check, Loader2, MessageSquare } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const OUTCOMES = [
  { v: "approved", label: "Customer approved", className: "bg-success text-success-foreground", testid: "cb-resolve-approved" },
  { v: "rejected", label: "Customer declined",  className: "bg-destructive text-destructive-foreground", testid: "cb-resolve-rejected" },
  { v: "pending",  label: "Still thinking — clear flag", className: "bg-secondary text-secondary-foreground", testid: "cb-resolve-pending" },
];

/**
 * Surfaces pending customer callback requests on the Job page so the advisor
 * can mark them as done after they've actually spoken to the customer.
 */
export default function CallbackRequestsPanel({ job, onChange }) {
  const requests = job?.callback_requests || [];
  const pending = requests
    .map((cb, idx) => ({ ...cb, idx }))
    .filter((cb) => cb.status !== "resolved");
  const [busyIdx, setBusyIdx] = useState(null);
  const [noteByIdx, setNoteByIdx] = useState({});

  if (!pending.length) return null;

  const itemNames = (ids) => {
    const set = new Set(ids || []);
    const items = (job?.items || []).filter((it) => set.has(it.id));
    if (!items.length) return "the quote";
    return items.map((it) => it.name).join(", ");
  };

  const resolve = async (cb, outcome) => {
    setBusyIdx(cb.idx);
    try {
      const r = await api.post(`/jobs/${job.id}/callback-requests/${cb.idx}/resolve`, {
        outcome,
        note: noteByIdx[cb.idx] || "",
      });
      onChange?.(r.data);
      toast.success(
        outcome === "approved" ? "Marked done — customer approved"
        : outcome === "rejected" ? "Marked done — customer declined"
        : "Marked done — flag cleared"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update — try again");
    } finally {
      setBusyIdx(null);
    }
  };

  return (
    <div
      className="card-default p-5 border-primary/40 bg-primary/[0.04]"
      data-testid="callback-requests-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex w-7 h-7 rounded-full bg-primary/15 items-center justify-center">
            <Phone className="w-3.5 h-3.5 text-primary" />
          </span>
          <div>
            <div className="label-overline text-primary/80">Customer wants a callback</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              {pending.length} pending {pending.length === 1 ? "request" : "requests"}
            </div>
          </div>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-primary/15 text-primary"
          data-testid="callback-pending-count"
        >
          {pending.length}
        </span>
      </div>

      <ul className="mt-4 space-y-3">
        {pending.map((cb) => {
          const phone = cb.phone || job?.customer_phone || "";
          const busy = busyIdx === cb.idx;
          return (
            <li
              key={cb.idx}
              className="rounded-lg border border-border bg-card p-4"
              data-testid={`callback-request-${cb.idx}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="text-sm">
                  <div className="font-semibold">
                    About: <span className="font-normal">{itemNames(cb.item_ids)}</span>
                  </div>
                  <div className="text-muted-foreground mt-1 text-xs">
                    Asked {timeAgo(cb.at)} · prefers <strong className="text-foreground">{(cb.preferred_time || "asap").toUpperCase()}</strong>
                  </div>
                </div>
                {phone && (
                  <a
                    href={`tel:${phone.replace(/\s/g, "")}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 whitespace-nowrap"
                    data-testid={`callback-call-link-${cb.idx}`}
                  >
                    <Phone className="w-3 h-3" /> Call {phone}
                  </a>
                )}
              </div>

              {cb.customer_note && (
                <div className="mt-3 flex gap-2 text-sm bg-muted/40 p-3 rounded">
                  <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <span className="italic">"{cb.customer_note}"</span>
                </div>
              )}

              <div className="mt-3">
                <label className="label-overline text-[10px]">Notes from the call (optional)</label>
                <textarea
                  rows={2}
                  className="w-full mt-1 text-sm rounded border border-border bg-background p-2"
                  placeholder="e.g. Spoke to John — happy to go ahead with the pads, wants to defer the alignment."
                  value={noteByIdx[cb.idx] || ""}
                  onChange={(e) => setNoteByIdx((s) => ({ ...s, [cb.idx]: e.target.value }))}
                  data-testid={`callback-note-${cb.idx}`}
                />
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {OUTCOMES.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    disabled={busy}
                    onClick={() => resolve(cb, o.v)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold disabled:opacity-50 hover:scale-[1.02] transition-transform ${o.className}`}
                    data-testid={`${o.testid}-${cb.idx}`}
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {o.label}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
